/**
 * Inject dynamic JSON-LD structured data into the document head for the
 * current page. Cleans up on unmount so switching routes doesn't leak
 * stale schemas.
 *
 * Usage:
 *   useJsonLd("faq-oncologia", {
 *     "@context": "https://schema.org",
 *     "@type": "FAQPage",
 *     mainEntity: [...]
 *   });
 *
 * The `id` (string) is used as the DOM id of the <script> tag; pass a
 * unique-per-page value.
 */
import { useEffect } from "react";

export const useJsonLd = (id, data) => {
  useEffect(() => {
    if (!data) return undefined;
    const domId = `jsonld-${id}`;
    let el = document.getElementById(domId);
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = domId;
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);
    return () => {
      const node = document.getElementById(domId);
      if (node && node.parentNode) node.parentNode.removeChild(node);
    };
  }, [id, data]);
};

/**
 * Build a FAQPage schema from a list of `{ q, a }` items (Spanish or Catalan).
 */
export const buildFaqSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: items.map((it) => ({
    "@type": "Question",
    name: it.q,
    acceptedAnswer: { "@type": "Answer", text: it.a },
  })),
});

/**
 * Build a BreadcrumbList schema for a blog article or any deep page.
 * `items` is an ordered array of `{ name, url }`.
 */
export const buildBreadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, idx) => ({
    "@type": "ListItem",
    position: idx + 1,
    name: it.name,
    item: it.url,
  })),
});

/**
 * Build a MedicalCondition schema for a topic-focused landing page
 * (Oncología, Fibromialgia, etc.).
 */
export const buildMedicalConditionSchema = ({ name, alternateName, description, url, associatedAnatomy }) => ({
  "@context": "https://schema.org",
  "@type": "MedicalCondition",
  name,
  alternateName,
  description,
  url,
  ...(associatedAnatomy ? { associatedAnatomy } : {}),
});

export default useJsonLd;
